import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Connect to backend
    console.log('Contact form:', formData);
    setSubmitted(true);
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-moody/30 to-brand-charcoal" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gold-500 tracking-[0.3em] uppercase text-sm mb-4">
            Get in Touch
          </p>
          <h1 className="text-4xl md:text-5xl font-serif text-warm-100 mb-6">
            Contact Us
          </h1>
          <div className="divider-gold max-w-xs mx-auto" />
          <p className="text-warm-100/70 text-lg max-w-2xl mx-auto mt-6">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <h2 className="text-2xl font-serif text-warm-100 mb-6">
              Reach Out
            </h2>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-gold-500" />
                </div>
                <div>
                  <p className="text-warm-100 font-medium mb-1">Email</p>
                  <a href="mailto:contact@dnfministry.com" className="text-warm-100/70 hover:text-gold-400 transition-colors">
                    contact@dnfministry.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-gold-500" />
                </div>
                <div>
                  <p className="text-warm-100 font-medium mb-1">Phone</p>
                  <p className="text-warm-100/70">Available upon request</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-gold-500" />
                </div>
                <div>
                  <p className="text-warm-100 font-medium mb-1">Service Area</p>
                  <p className="text-warm-100/70">Travel available - inquire for details</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-gold-500" />
                </div>
                <div>
                  <p className="text-warm-100 font-medium mb-1">Response Time</p>
                  <p className="text-warm-100/70">Within 24-48 hours</p>
                </div>
              </div>
            </div>

            {/* Scripture */}
            <div className="mt-12 p-6 card-elegant">
              <p className="scripture-quote text-sm">
                "...He considered me faithful by assigning me to a ministry."
                <span className="block mt-2 text-gold-500/80 not-italic">— 1 Timothy 1:12</span>
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="card-elegant p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-gold-500/20 flex items-center justify-center mx-auto mb-6">
                  <Send className="w-8 h-8 text-gold-500" />
                </div>
                <h3 className="text-2xl font-serif text-warm-100 mb-4">
                  Message Sent!
                </h3>
                <p className="text-warm-100/70">
                  Thank you for reaching out. We'll be in touch soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="card-elegant p-8 md:p-12">
                <h2 className="text-2xl font-serif text-warm-100 mb-6">
                  Send a Message
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-warm-100/70 text-sm mb-2">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-warm-100/70 text-sm mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-warm-100/70 text-sm mb-2">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors"
                    data-testid="contact-subject"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-warm-100/70 text-sm mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full bg-brand-charcoal border border-gold-500/30 rounded-sm px-4 py-3 text-warm-100 focus:outline-none focus:border-gold-500 transition-colors resize-none"
                    data-testid="contact-message"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-gold w-full text-sm tracking-[0.2em] uppercase flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  <Send className="w-5 h-5" />
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
